import {
  nextBoundary,
  type RuleAction,
  type RuleEvery,
  type RuleSettings,
  type RuleTrigger,
  resetEvery,
} from '@mogzo/shared';
import { Badge, Button, Field, Input, Notice, Panel, Select, Switch, Textarea } from '@mogzo/ui';
import { type FormEvent, useState } from 'react';
import { ApiError, apiSend, type Project } from '../lib/api';
import { atDay, clockOf, minutesOf, type Schedule, type ScheduleRule } from '../lib/schedule';

type Env = 'test' | 'prod';

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

const choiceClass = (on: boolean) =>
  `flex min-h-11 flex-col gap-0.5 rounded-[6px] border px-3.5 py-2.5 text-left font-medium ${
    on ? 'border-ink shadow-[inset_0_0_0_1px_var(--color-ink)]' : 'border-control'
  } bg-card disabled:cursor-not-allowed disabled:border-dashed disabled:text-muted`;

function Choices<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: [T, string, string][];
  onChange: (value: T) => void;
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2.5">
      {options.map(([option, label, hint]) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={choiceClass(value === option)}
        >
          {label}
          <span className="text-[13px] font-normal text-muted">{hint}</span>
        </button>
      ))}
    </div>
  );
}

type ValueKind = 'random' | 'date' | 'fixed';

/**
 * Making or editing a schedule rule (M37, the approved mockup's "New rule"): when it runs (a
 * board starting fresh, an event ending, or set times) and what it does (an achievement for the
 * top players, or a config value).
 */
export function RuleForm({
  project,
  env,
  schedule,
  rule,
  onDone,
  onTrashed,
  onCancel,
}: {
  project: Project;
  env: Env;
  schedule: Schedule;
  rule: ScheduleRule | null;
  onDone: () => void;
  /** After Delete: it went to Trash, in this group (for Undo). */
  onTrashed: (group: string) => void;
  onCancel: () => void;
}) {
  const resetting = schedule.boards.filter((b) => resetEvery(b.reset));
  const ranked = schedule.events.filter((e) => e.board);
  const [trigger, setTrigger] = useState<RuleTrigger>(rule?.trigger ?? 'board_reset');
  const [board, setBoard] = useState(rule?.board ?? resetting[0]?.id ?? '');
  const [event, setEvent] = useState(rule?.event ?? schedule.events[0]?.id ?? '');
  const [every, setEvery] = useState<RuleEvery>(rule?.every ?? 'day');
  const [time, setTime] = useState(clockOf(rule?.atMinute ?? 0));
  const [action, setAction] = useState<RuleAction>(rule?.action ?? 'achievement');
  const [top, setTop] = useState(String(rule?.top ?? 10));
  const [achievement, setAchievement] = useState(
    rule?.achievement ?? schedule.achievements[0]?.id ?? '',
  );
  const [key, setKey] = useState(rule?.key ?? '');
  const value = rule?.value;
  const [valueKind, setValueKind] = useState<ValueKind>(value?.kind ?? 'random');
  const [min, setMin] = useState(String(value?.kind === 'random' ? value.min : 1));
  const [max, setMax] = useState(String(value?.kind === 'random' ? value.max : 99999));
  const [fixed, setFixed] = useState(
    value?.kind === 'fixed' ? JSON.stringify(value.value, null, 2) : '',
  );
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ran, setRan] = useState<{ status: 'done' | 'failed'; message: string } | null>(null);
  const base = `/v1/dev/projects/${project.id}/rules`;
  const where = env === 'prod' ? 'live' : 'test';

  function settings(): RuleSettings | string {
    const at = minutesOf(time);
    if (trigger === 'times' && at === null) return 'The time is like 00:00.';
    let ruleValue: RuleSettings['value'] = null;
    if (action === 'config') {
      if (valueKind === 'random')
        ruleValue = { kind: 'random', min: Number(min), max: Number(max) };
      else if (valueKind === 'date') ruleValue = { kind: 'date' };
      else {
        try {
          ruleValue = { kind: 'fixed', value: JSON.parse(fixed) };
        } catch {
          return 'The value isn’t valid JSON. Text goes in quotes: "night".';
        }
      }
    }
    return {
      trigger,
      board: trigger === 'board_reset' ? board : null,
      event: trigger === 'event_end' ? event : null,
      every: trigger === 'times' ? every : null,
      atMinute: trigger === 'times' ? at : null,
      action,
      top: action === 'achievement' ? Number(top) : null,
      achievement: action === 'achievement' ? achievement : null,
      key: action === 'config' ? key.trim() : null,
      value: ruleValue,
      enabled,
    };
  }

  async function save(submitted: FormEvent) {
    submitted.preventDefault();
    const next = settings();
    if (typeof next === 'string') return setError(next);
    setBusy(true);
    setError(null);
    try {
      if (rule) await apiSend('PUT', `${base}/${rule.id}?env=${env}`, next);
      else await apiSend('POST', `${base}?env=${env}`, next);
      onDone();
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  async function remove() {
    if (!rule) return;
    setBusy(true);
    try {
      const { group } = await apiSend<{ group: string }>('DELETE', `${base}/${rule.id}?env=${env}`);
      onTrashed(group);
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  async function runNow() {
    if (!rule) return;
    setBusy(true);
    setError(null);
    try {
      setRan(
        await apiSend<{ status: 'done' | 'failed'; message: string }>(
          'POST',
          `${base}/${rule.id}/run`,
        ),
      );
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  // The next three times it runs, as set now.
  const upcoming = (() => {
    const now = new Date(schedule.now);
    const times: { at: string; what: string }[] = [];
    if (trigger === 'event_end') {
      const chosen = schedule.events.find((e) => e.id === event);
      const end = chosen?.run?.end ?? chosen?.next?.end;
      if (chosen && end) times.push({ at: end, what: `When ${chosen.name} ends` });
      return times;
    }
    const chosenBoard = schedule.boards.find((b) => b.id === board);
    const cycle = trigger === 'times' ? every : chosenBoard ? resetEvery(chosenBoard.reset) : null;
    const minute = trigger === 'times' ? (minutesOf(time) ?? 0) : 0;
    if (!cycle) return times;
    let next = nextBoundary(cycle, minute, now);
    while (times.length < 3) {
      times.push({
        at: next.toISOString(),
        what: trigger === 'times' ? `Every ${cycle}` : `${chosenBoard?.name}, the ${cycle} before`,
      });
      next = nextBoundary(cycle, minute, next);
    }
    return times;
  })();

  return (
    <div className="flex flex-wrap items-start gap-6">
      <form
        onSubmit={save}
        aria-labelledby="rule-form-heading"
        className="mz-panel flex min-w-0 flex-[999_1_560px] flex-col"
      >
        <header className="mz-panel-head">
          <div>
            <h2 id="rule-form-heading" className="mz-text-title-lg m-0">
              {rule ? 'Edit rule' : 'New rule'}
            </h2>
            <p className="mz-panel-desc">
              {env === 'test'
                ? 'Your test community gets it right away. Copy it to live when it’s right.'
                : 'It runs for your live players at its times.'}
            </p>
          </div>
          <Badge tone={env === 'test' ? 'info' : 'success'}>
            {env === 'test' ? 'Test schedule' : 'Live schedule'}
          </Badge>
        </header>

        <div className="mz-panel-body flex flex-col gap-7">
          {error && <Notice tone="danger">{error}</Notice>}

          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="mb-3 p-0 text-[13px] font-semibold">When</legend>
            <Choices
              value={trigger}
              onChange={setTrigger}
              options={[
                ['board_reset', 'A leaderboard starts fresh', 'Every day, week or month'],
                ['event_end', 'An event ends', 'Each time it turns off'],
                ['times', 'At set times', 'Every day, week or month'],
              ]}
            />
            {trigger === 'board_reset' &&
              (resetting.length ? (
                <Field
                  className="max-w-[420px]"
                  label="Leaderboard"
                  htmlFor="r-board"
                  hint="Boards that start fresh. It runs just after the reset, on the period that ended."
                >
                  <Select
                    id="r-board"
                    value={board}
                    onChange={(change) => setBoard(change.target.value)}
                    options={resetting.map((b) => ({
                      value: b.id,
                      label: `${b.name} · ${b.reset === 'daily' ? 'every day' : b.reset === 'weekly' ? 'every Monday' : 'on the 1st'}`,
                    }))}
                  />
                </Field>
              ) : (
                <Notice tone="info">
                  The {where} schedule has no leaderboard that starts fresh.
                </Notice>
              ))}
            {trigger === 'event_end' &&
              (schedule.events.length ? (
                <Field
                  className="max-w-[420px]"
                  label="Event"
                  htmlFor="r-event"
                  hint="It runs each time the event turns off, on that run’s own leaderboard."
                >
                  <Select
                    id="r-event"
                    value={event}
                    onChange={(change) => setEvent(change.target.value)}
                    options={schedule.events.map((e) => ({ value: e.id, label: e.name }))}
                  />
                </Field>
              ) : (
                <Notice tone="info">Make an event first.</Notice>
              ))}
            {trigger === 'times' && (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
                <Field label="How often" htmlFor="r-every">
                  <Select
                    id="r-every"
                    value={every}
                    onChange={(change) => setEvery(change.target.value as RuleEvery)}
                    options={[
                      { value: 'day', label: 'Every day' },
                      { value: 'week', label: 'Every Monday' },
                      { value: 'month', label: 'On the 1st of each month' },
                    ]}
                  />
                </Field>
                <Field label="At" htmlFor="r-at" hint="UTC.">
                  <Input
                    id="r-at"
                    mono
                    value={time}
                    onChange={(change) => setTime(change.target.value)}
                  />
                </Field>
              </div>
            )}
          </fieldset>

          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="mb-3 p-0 text-[13px] font-semibold">Do</legend>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2.5">
              {(
                [
                  ['achievement', 'Give an achievement', 'To the top players'],
                  ['config', 'Set a config value', 'Like a new daily seed'],
                ] as const
              ).map(([option, label, hint]) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={action === option}
                  onClick={() => setAction(option)}
                  className={choiceClass(action === option)}
                >
                  {label}
                  <span className="text-[13px] font-normal text-muted">{hint}</span>
                </button>
              ))}
              <button type="button" disabled className={choiceClass(false)}>
                Post a recap
                <span className="text-[13px] font-normal text-muted">Comes with game posts</span>
              </button>
            </div>

            {action === 'achievement' &&
              (trigger === 'times' ? (
                <Notice tone="warning" title="This needs a leaderboard">
                  At set times there’s no board to rank, so pick “A leaderboard starts fresh” or “An
                  event ends” to give an achievement to the top players.
                </Notice>
              ) : (
                <>
                  <div className="grid grid-cols-[140px_repeat(auto-fit,minmax(240px,1fr))] gap-4">
                    <Field label="To the top" htmlFor="r-top">
                      <Input
                        id="r-top"
                        inputMode="numeric"
                        value={top}
                        onChange={(change) => setTop(change.target.value)}
                      />
                    </Field>
                    <Field
                      label="Achievement"
                      htmlFor="r-achievement"
                      hint={
                        env === 'prod'
                          ? 'Live achievements come with a published version.'
                          : 'Made in Data › Achievements. Live needs it published too.'
                      }
                    >
                      <Select
                        id="r-achievement"
                        value={achievement}
                        onChange={(change) => setAchievement(change.target.value)}
                        options={schedule.achievements.map((a) => ({ value: a.id, label: a.name }))}
                      />
                    </Field>
                  </div>
                  <p className="mz-hint m-0">
                    Ranked on{' '}
                    {trigger === 'event_end' ? 'the run’s own board' : 'the period that just ended'}
                    . Players tied at the last place get it too; players who have it already keep
                    it.
                  </p>
                  {trigger === 'event_end' && !ranked.some((e) => e.id === event) && (
                    <Notice tone="warning">
                      This event has no leaderboard of its own to rank. Give it one first.
                    </Notice>
                  )}
                </>
              ))}

            {action === 'config' && (
              <>
                <Field
                  className="max-w-[420px]"
                  label="Config value"
                  htmlFor="r-key"
                  hint={
                    key && key in schedule.config
                      ? `Now ${JSON.stringify(schedule.config[key])} in ${where} config.`
                      : undefined
                  }
                >
                  <Input
                    id="r-key"
                    mono
                    value={key}
                    placeholder="dailySeed"
                    onChange={(change) => setKey(change.target.value)}
                  />
                </Field>
                <span className="mz-label">Set it to</span>
                <Choices
                  value={valueKind}
                  onChange={setValueKind}
                  options={[
                    ['random', 'A random number', 'A whole number'],
                    ['date', 'Today’s date', '"2026-11-04"'],
                    ['fixed', 'A value', 'The same each time'],
                  ]}
                />
                {valueKind === 'random' && (
                  <div className="grid grid-cols-[repeat(2,minmax(0,160px))] gap-4">
                    <Field label="From" htmlFor="r-min">
                      <Input
                        id="r-min"
                        inputMode="numeric"
                        value={min}
                        onChange={(change) => setMin(change.target.value)}
                      />
                    </Field>
                    <Field label="To" htmlFor="r-max">
                      <Input
                        id="r-max"
                        inputMode="numeric"
                        value={max}
                        onChange={(change) => setMax(change.target.value)}
                      />
                    </Field>
                  </div>
                )}
                {valueKind === 'fixed' && (
                  <Field label="Value, as JSON" htmlFor="r-json">
                    <Textarea
                      id="r-json"
                      mono
                      rows={3}
                      value={fixed}
                      onChange={(change) => setFixed(change.target.value)}
                    />
                  </Field>
                )}
                <p className="mz-hint m-0">
                  Your game reads it with <code className="mz-code">mogzo.config.get()</code>. Games
                  already running get it the next time they start.
                </p>
              </>
            )}
          </fieldset>

          <div className="flex flex-col gap-1.5">
            <Switch
              label="On"
              checked={enabled}
              onChange={(change) => setEnabled(change.target.checked)}
            />
            <p className="mz-hint m-0">Turn it off to stop it running without deleting it.</p>
          </div>
        </div>

        <footer className="mz-panel-foot bg-card">
          <div className="flex flex-wrap gap-2.5">
            <Button type="submit" variant="primary" loading={busy}>
              {rule ? 'Save changes' : 'Create rule'}
            </Button>
            <Button onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          </div>
          {rule && (
            <Button variant="danger" onClick={remove} disabled={busy}>
              Delete rule
            </Button>
          )}
        </footer>
      </form>

      <div className="flex max-w-[400px] min-w-0 flex-[1_1_300px] flex-col gap-6">
        <Panel title="Next runs" description="As set now. Times are UTC." flush>
          {upcoming.length ? (
            <ul className="m-0 list-none p-0">
              {upcoming.map((run) => (
                <li
                  key={run.at}
                  className="flex flex-col gap-0.5 border-t border-line px-5 py-3 first:border-t-0"
                >
                  <span className="font-medium">{atDay(run.at)}</span>
                  <span className="text-[13px] text-muted">{run.what}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 px-5 py-4 text-[13px] text-muted">Nothing ahead as set.</p>
          )}
        </Panel>
        {env === 'test' && (
          <Panel
            title="Try it in test"
            description={
              rule
                ? 'Runs it once now for your test community, on the board’s current period or the event’s last run. Live is never touched.'
                : 'Once it’s made, Run now runs it once for your test community. Live is never touched.'
            }
          >
            {rule && (
              <div className="flex flex-col gap-3">
                {ran && (
                  <Notice tone={ran.status === 'done' ? 'success' : 'danger'}>{ran.message}</Notice>
                )}
                <div>
                  <Button onClick={runNow} loading={busy}>
                    Run now
                  </Button>
                </div>
              </div>
            )}
          </Panel>
        )}
      </div>
    </div>
  );
}
